import { elementChildren, firstDescendant } from './dom';
import { LandxmlNonMetreUnitsError } from './tin';

/**
 * Raw declared `linearUnit` string from `<Units>`, or null if none is
 * declared. Scans ALL of `<Units>`'s children for the first with a
 * `linearUnit` attribute — not just child[0], which may be a leading
 * `<Feature/>`. No validation, no table lookup: just "what did the file
 * say". The one place in this module that reads the attribute; every guard
 * below goes through `assertMetreUnits`.
 */
function declaredLinearUnit(doc: Document): string | null {
  const unitsEl = firstDescendant(doc, 'Units');
  if (!unitsEl) return null;
  for (const child of elementChildren(unitsEl)) {
    const candidate = child.getAttribute('linearUnit');
    if (candidate !== null) return candidate;
  }
  return null;
}

/** The only `linearUnit` spellings these readers render (lower-cased, trimmed). */
const METRE_UNITS = new Set(['meter', 'metre']);

/**
 * DOM counterpart to `assertLandxmlMetres` in `landxmlTin.ts` — same rule,
 * same error, for documents already parsed into a `Document` rather than read
 * from raw text: metres pass, an absent `<Units>` (or no `linearUnit` on any
 * of its children) is assumed to be metres with a console.warn, and every
 * other declared unit throws `LandxmlNonMetreUnitsError`.
 *
 * Nothing here scales. Surfaces once did (the TIN reader's table, mirrored
 * from `scripts/surface_io.py`), but alignments, breaklines, parcels and pipe
 * networks never could: their curve radius/length attributes, station
 * equations, profile data, parcel areas and pipe diameters are all plain
 * numbers in the file's own unit. A half-converted file — points scaled,
 * radii not — looks plausible and is worse than a refused one, and the
 * server-side reader refuses non-metre files outright, so the client agreeing
 * is the whole point (corridor-trust T5).
 */
export function assertMetreUnits(doc: Document): void {
  const unit = declaredNonMetreUnit(doc);
  if (unit !== null) throw new LandxmlNonMetreUnitsError(unit);
}

/**
 * The declared unit when it is one this reader refuses, else null (metres, or
 * an absent `<Units>` — warned about once, here). Separate from
 * `assertMetreUnits` so the parsers below can resolve (and warn) once at the
 * top while still throwing only where they would actually have rendered
 * something: a file whose `<Parcels>`/`<Breaklines>` container is empty has
 * nothing to refuse.
 */
export function declaredNonMetreUnit(doc: Document): string | null {
  const linearUnit = declaredLinearUnit(doc);
  if (linearUnit === null) {
    console.warn('LandXML: no <Units>/linearUnit found; assuming metres.');
    return null;
  }
  return METRE_UNITS.has(linearUnit.trim().toLowerCase()) ? null : linearUnit;
}
